import React, { useState, useEffect, useMemo } from "react";
import { Modal, Input, Radio, Space, Button, Tag, message } from "antd";
import { CloseCircleOutlined } from "@ant-design/icons";
import { ChecklistAnswerType, ConditionalAction } from "../../../MasterData/checklistTypes";
import { RootState, useAppSelector } from "../../../../../store";
import { getFullAwsUrl } from "../../../../../helpers/utils";
import { BtnLoader } from "../../../../../Components/Components";
import { ChecklistFileUploadModal } from "../../../Quotations/Components/ChecklistFileUploadModal";
// import {
//   ChecklistAnswerType,
//   ConditionalAction,
// } from "../../MasterData/checklistTypes";
// import { ChecklistFileUploadModal } from "../../Quotations/Components/ChecklistFileUploadModal";
// import { getFullAwsUrl } from "../../../../helpers/utils";
// import { RootState, useAppSelector } from "../../../../store";
// import { BtnLoader } from "../../../../Components/Components";

const { TextArea } = Input;

interface Props {
  visible: boolean;
  setIsModalVisible: (visible: boolean) => void;
  selectedLoadData: any;
  onSave: (formattedDispatchChecklist: any) => void;
  loading?: boolean;
}

const getQId = (q: any) => q.question_id || q._id;

const FillDispatchChecklistModal: React.FC<Props> = ({
  visible,
  setIsModalVisible,
  selectedLoadData,
  onSave,
  loading = false,
}) => {
  const [answers, setAnswers] = useState<any>({});
  const [uploadModal, setUploadModal] = useState<any>({
    open: false,
    questionId: null,
    questionText: "",
    isConditional: false,
    isPhotoRequiredAction: false,
    ansType: ChecklistAnswerType.TEXT,
  });

  const { updateDispatchStatusLoading } = useAppSelector(
    (state: RootState) => state.loading,
  );

  const dispatchChecklist = useMemo(() => {
    return selectedLoadData?.dispatch_checklist || [];
  }, [selectedLoadData]);

  const allQuestions = useMemo(() => {
    return dispatchChecklist.flatMap((c: any) => c.questions_data || []);
  }, [dispatchChecklist]);

  useEffect(() => {
    if (visible && selectedLoadData) {
      const initAns: any = {};
      (selectedLoadData?.dispatch_checklist || []).forEach((c: any) => {
        (c?.questions_data || []).forEach((q: any) => {
          const qId = getQId(q);
          if (qId) {
            let ans = q.answer !== undefined ? q.answer : null;
            if (q.ans_type === ChecklistAnswerType.YES_NO || q.ans_type === "YES_NO") {
              if (ans === true) ans = "YES";
              if (ans === false) ans = "NO";
            }
            initAns[qId] = {
              ans_type: q.ans_type,
              answer: ans,
              conditional_answer:
                q.conditional_answer !== undefined ? q.conditional_answer : null,
              photo: q.photo || [],
              is_answered: q.is_answered || false,
            };
          }
        });
      });
      setAnswers(initAns);
    }
  }, [visible, selectedLoadData]);

  const handleAnswerChange = (
    questionId: string,
    type: ChecklistAnswerType,
    value: any,
    isConditional: boolean = false,
  ) => {
    setAnswers((prev: any) => {
      const currentEntry = prev[questionId] || { ans_type: type, answer: null };
      if (isConditional) {
        return {
          ...prev,
          [questionId]: { ...currentEntry, conditional_answer: value },
        };
      }
      const newState = {
        ...prev,
        [questionId]: { ...currentEntry, answer: value },
      };
      if (type === ChecklistAnswerType.YES_NO) {
        newState[questionId].conditional_answer = null;
      }
      return newState;
    });
  };

  const handleOpenUpload = (
    question: any,
    isConditional: boolean,
    isPhotoRequiredAction: boolean = false,
  ) => {
    const qId = getQId(question);
    setUploadModal({
      open: true,
      questionId: qId,
      questionText: question.question_text,
      isConditional: isConditional,
      isPhotoRequiredAction: isPhotoRequiredAction,
      ansType: question.ans_type,
    });
  };

  const handleCloseUpload = () => {
    setUploadModal((prev: any) => ({ ...prev, open: false }));
  };

  const handleSaveUpload = (fileKey: string) => {
    const { questionId, isConditional, isPhotoRequiredAction, ansType } =
      uploadModal;
    if (!questionId) return;

    const currentEntry = answers[questionId] || {
      ans_type: ansType,
      answer: null,
    };

    let currentFiles: string[] = [];
    if (isPhotoRequiredAction) {
      currentFiles = (currentEntry?.photo as string[]) || [];
    } else if (isConditional) {
      currentFiles = (currentEntry?.conditional_answer as string[]) || [];
    } else {
      currentFiles = (currentEntry?.answer as string[]) || [];
    }

    const newFiles = Array.isArray(currentFiles)
      ? [...currentFiles, fileKey]
      : [fileKey];

    if (isPhotoRequiredAction) {
      setAnswers((prev: any) => ({
        ...prev,
        [questionId]: {
          ...(prev[questionId] || { ans_type: ansType, answer: null }),
          photo: newFiles,
        },
      }));
    } else {
      handleAnswerChange(questionId, ansType, newFiles, isConditional);
    }

    handleCloseUpload();
  };

  const handleRemoveFile = (
    questionId: string,
    fileKeyToRemove: string,
    isConditional: boolean,
    ansType: ChecklistAnswerType,
    isPhotoRequiredAction: boolean = false,
  ) => {
    const currentEntry = answers[questionId];
    if (!currentEntry) return;

    let currentFiles: any[] = [];
    if (isPhotoRequiredAction) {
      currentFiles = (currentEntry.photo as string[]) || [];
    } else if (isConditional) {
      currentFiles = (currentEntry.conditional_answer as string[]) || [];
    } else {
      currentFiles = (currentEntry.answer as string[]) || [];
    }

    if (!Array.isArray(currentFiles)) return;

    const newFiles = currentFiles.filter((key) => key !== fileKeyToRemove);

    if (isPhotoRequiredAction) {
      setAnswers((prev: any) => ({
        ...prev,
        [questionId]: { ...prev[questionId], photo: newFiles },
      }));
    } else {
      handleAnswerChange(questionId, ansType, newFiles, isConditional);
    }
  };

  const getFileNameFromUrl = (key: string) => {
    try {
      const parts = key.split("/");
      const fullName = parts[parts.length - 1];
      if (fullName.includes("_") && fullName.length > 37) {
        return fullName.substring(fullName.indexOf("_") + 1);
      }
      return fullName;
    } catch (e) {
      return "File";
    }
  };

  const renderFileTags = (
    fileKeys: string[] | null | undefined,
    question: any,
    isConditional: boolean,
    isPhotoRequiredAction: boolean = false,
  ) => {
    if (!fileKeys || !Array.isArray(fileKeys) || fileKeys.length === 0)
      return null;

    const qId = getQId(question);

    return (
      <div className="mt-2 d-flex flex-wrap gap-2">
        {fileKeys.map((key, index) => (
          <Tag
            key={index}
            closable
            onClose={(e) => {
              e.preventDefault();
              handleRemoveFile(
                qId,
                key,
                isConditional,
                question.ans_type,
                isPhotoRequiredAction,
              );
            }}
            closeIcon={
              <CloseCircleOutlined className="text-danger fs-6 ms-2" />
            }
            className="d-inline-flex align-items-center px-3 py-1 bg-white border rounded-2 fs-6 fw-semibold cursor-pointer text-wrap"
            style={{
              border: "1px solid #E0E0E0",
              whiteSpace: "normal",
              wordBreak: "break-all",
              maxWidth: "100%",
              height: "auto",
            }}
          >
            <span
              onClick={() => window.open(getFullAwsUrl(key), "_blank")}
              title={getFileNameFromUrl(key)}
              style={{
                whiteSpace: "normal",
                wordBreak: "break-all",
                maxWidth: "100%",
              }}
            >
              {getFileNameFromUrl(key)}
            </span>
          </Tag>
        ))}
      </div>
    );
  };

  const renderRequiredPhotoSection = (question: any) => {
    if (!question.photo_required) return null;
    const qId = getQId(question);
    const currentPhotoFiles = (answers[qId]?.photo as string[]) || [];

    return (
      <div className="mt-3 border-top pt-2">
        <div className="d-flex justify-content-between align-items-center">
          <span className="fw-semibold fs-7 text-secondary">
            Photo Required <span className="text-danger">*</span>
          </span>
          <Button
            type="dashed"
            size="small"
            onClick={() => handleOpenUpload(question, false, true)}
          >
            Upload Photo
          </Button>
        </div>
        {renderFileTags(currentPhotoFiles, question, false, true)}
      </div>
    );
  };

  const renderConditionalInput = (question: any) => {
    const qId = getQId(question);
    const currentAnswer = answers[qId]?.answer;
    if (currentAnswer === undefined || currentAnswer === null) return null;
    const isYes = currentAnswer === "YES" || currentAnswer === true;
    const isNo = currentAnswer === "NO" || currentAnswer === false;
    const action = isYes ? question.if_yes_action : isNo ? question.if_no_action : null;

    if (!action || action === ConditionalAction.N_A) return null;
    const subValue = answers[qId]?.conditional_answer;

    switch (action) {
      case ConditionalAction.TEXT:
        return (
          <Input
            className="form-control mt-2"
            placeholder="Please specify details..."
            value={(subValue as string) || ""}
            onChange={(e) =>
              handleAnswerChange(
                qId,
                question.ans_type,
                e.target.value,
                true,
              )
            }
          />
        );
      case ConditionalAction.LONG_TEXT:
        return (
          <TextArea
            className="form-control mt-2"
            rows={3}
            placeholder="Please explain..."
            value={(subValue as string) || ""}
            onChange={(e) =>
              handleAnswerChange(
                qId,
                question.ans_type,
                e.target.value,
                true,
              )
            }
          />
        );
      case ConditionalAction.FILE_UPLOAD:
        return (
          <>
            <div className="mt-2 border-top pt-2 d-flex justify-content-end align-items-center gap-2">
              <Button
                type="primary"
                className="bg-dark border-0 rounded-2 px-4 fw-bold"
                onClick={() => handleOpenUpload(question, true)}
              >
                UPLOAD
              </Button>
            </div>
            {renderFileTags(subValue as string[], question, true)}
          </>
        );
      default:
        return null;
    }
  };

  const renderQuestionItem = (question: any) => {
    const qId = getQId(question);
    const entry = answers[qId] || {};
    const val = entry.answer;
    switch (question.ans_type) {
      case ChecklistAnswerType.TEXT:
        return (
          <div key={qId} className="border rounded-2 py-2 ps-sm-3 pe-2 ps-2">
            <div className="d-flex flex-sm-row flex-column justify-content-between align-items-sm-center gap-2">
              <span className="fw-bold fs-6 lh-m">
                {question.question_text}
              </span>
              <Input
                className="form-control checklist-input"
                placeholder="Type answer..."
                value={(val as string) || ""}
                onChange={(e) =>
                  handleAnswerChange(
                    qId,
                    question.ans_type,
                    e.target.value,
                  )
                }
              />
            </div>
            {renderRequiredPhotoSection(question)}
          </div>
        );
      case ChecklistAnswerType.LONG_TEXT:
        return (
          <div key={qId} className="border rounded-2 py-2 ps-sm-3 pe-2 ps-2">
            <p className="fw-bold fs-6 lh-m mb-2">{question.question_text}</p>
            <TextArea
              rows={4}
              className="form-control"
              placeholder="Type details..."
              value={(val as string) || ""}
              onChange={(e) =>
                handleAnswerChange(
                  qId,
                  question.ans_type,
                  e.target.value,
                )
              }
            />
            {renderRequiredPhotoSection(question)}
          </div>
        );
      case ChecklistAnswerType.YES_NO:
        return (
          <div key={qId} className="border rounded-2 py-2 ps-sm-3 pe-2 ps-2">
            <div className="d-flex flex-sm-row flex-column justify-content-between align-items-sm-center mb-0 w-100 gap-2">
              <span className="fw-bold fs-6 lh-m">
                {question.question_text}
              </span>
              <Radio.Group
                value={val === true ? "YES" : val === false ? "NO" : val}
                onChange={(e) =>
                  handleAnswerChange(
                    qId,
                    question.ans_type,
                    e.target.value,
                  )
                }
              >
                <Space direction="horizontal">
                  <Radio
                    value="YES"
                    className="fs-6 text-primary customRadioButton"
                  >
                    Yes
                  </Radio>
                  <Radio
                    value="NO"
                    className="fs-6 text-primary customRadioButton"
                  >
                    No
                  </Radio>
                </Space>
              </Radio.Group>
            </div>
            {renderConditionalInput(question)}
            {renderRequiredPhotoSection(question)}
          </div>
        );
      case ChecklistAnswerType.FILE_UPLOAD:
        return (
          <div key={qId} className="border rounded-2 py-2 ps-sm-3 pe-2 ps-2">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-md-3 gap-2">
              <span className="fw-bold fs-6 lh-m">
                {question.question_text}
              </span>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Button
                  type="primary"
                  className="bg-dark border-0 rounded-2 px-4 ms-md-2 fw-bold"
                  onClick={() => handleOpenUpload(question, false)}
                >
                  UPLOAD
                </Button>
              </div>
            </div>
            {renderFileTags(val as string[], question, false)}
            {renderRequiredPhotoSection(question)}
          </div>
        );
      default:
        return null;
    }
  };

  const handleModalSave = () => {
    if (allQuestions && allQuestions.length > 0) {
      for (const q of allQuestions) {
        const qId = getQId(q);
        const entry = answers[qId];

        // 1. Validate Main Answer
        if (
          !entry ||
          entry?.answer === undefined ||
          entry?.answer === null ||
          (typeof entry?.answer === "string" && entry?.answer.trim() === "")
        ) {
          if (Array.isArray(entry?.answer) && entry.answer.length === 0) {
            message.error(
              `Answer is required for question: "${q.question_text}"`,
            );
            return;
          } else if (!Array.isArray(entry?.answer)) {
            message.error(
              `Answer is required for question: "${q.question_text}"`,
            );
            return;
          }
        }

        if (Array.isArray(entry?.answer) && entry.answer.length === 0) {
          message.error(
            `Answer is required for question: "${q.question_text}"`,
          );
          return;
        }

        // 2. Validate Conditional Answer (if applicable)
        if (q.ans_type === ChecklistAnswerType.YES_NO || q.ans_type === "YES_NO") {
          const isYes = entry.answer === "YES" || entry.answer === true;
          const action = isYes ? q.if_yes_action : q.if_no_action;
          if (action && action !== ConditionalAction.N_A) {
            if (
              entry.conditional_answer === undefined ||
              entry.conditional_answer === null ||
              (typeof entry.conditional_answer === "string" &&
                entry.conditional_answer.trim() === "")
            ) {
              if (
                Array.isArray(entry?.conditional_answer) &&
                entry.conditional_answer.length === 0
              ) {
                message.error(
                  `Conditional answer is required for question: "${q.question_text}"`,
                );
                return;
              } else if (!Array.isArray(entry?.conditional_answer)) {
                message.error(
                  `Conditional answer is required for question: "${q.question_text}"`,
                );
                return;
              }
            }

            if (
              Array.isArray(entry.conditional_answer) &&
              entry.conditional_answer.length === 0
            ) {
              message.error(
                `Conditional answer is required for question: "${q.question_text}"`,
              );
              return;
            }
          }
        }

        // 3. Validate Photo (if required)
        if (q.photo_required) {
          if (!entry || !entry.photo || entry.photo.length === 0) {
            message.error(
              `Photo is required for question: "${q.question_text}"`,
            );
            return;
          }
        }
      }
    }

    const formattedDispatchChecklist = (
      selectedLoadData?.dispatch_checklist || []
    ).map((chkGroup: any) => {
      const questions = chkGroup.questions_data || [];
      const formattedQuestions = questions.map((q: any) => {
        const qId = getQId(q);
        const entry = answers[qId] || {};
        let finalAnswer = entry.answer !== undefined ? entry.answer : null;
        if (q.ans_type === ChecklistAnswerType.YES_NO || q.ans_type === "YES_NO") {
          if (finalAnswer === true) finalAnswer = "YES";
          if (finalAnswer === false) finalAnswer = "NO";
        }
        const isQAnswered =
          finalAnswer !== null &&
          finalAnswer !== undefined &&
          finalAnswer !== "";
        return {
          question_id: qId,
          question_text: q.question_text || "",
          ans_type: q.ans_type || entry.ans_type,
          answer: finalAnswer,
          conditional_answer:
            entry.conditional_answer !== undefined
              ? entry.conditional_answer
              : null,
          photo: entry.photo || [],
          is_answered:
            entry.is_answered !== undefined ? entry.is_answered : isQAnswered,
          if_yes_action: q.if_yes_action || null,
          if_no_action: q.if_no_action || null,
          photo_required: q.photo_required ?? false,
        };
      });
      return {
        checklist_id: chkGroup.checklist_id || chkGroup._id,
        questions_data: formattedQuestions,
      };
    });

    onSave(formattedDispatchChecklist);
  };

  const isBtnLoading = loading || updateDispatchStatusLoading;

  return (
    <>
      <Modal
        visible={visible}
        title={`Dispatch Checklist Questions - Load ${selectedLoadData?.load_number || ""}`}
        onCancel={() => setIsModalVisible(false)}
        footer={
          <div className="d-flex justify-content-center gap-4">
            <button
              type="button"
              className="btn btn-lg btn-outline-secondary min-w-130"
              onClick={() => {
                setIsModalVisible(false);
              }}
            >
              CLOSE
            </button>
            <button
              onClick={handleModalSave}
              className={`btn btn-lg min-w-130 btn-primary`}
              disabled={isBtnLoading}
            >
              {isBtnLoading ? <BtnLoader /> : "SAVE CHECKLIST"}
            </button>
          </div>
        }
        centered
        width={"60rem"}
        bodyStyle={{ maxHeight: "60vh", overflowY: "auto" }}
      >
        <div className="d-flex flex-column gap-3 p-2 mt-2">
          {allQuestions?.length > 0 ? (
            allQuestions.map((q: any) => renderQuestionItem(q))
          ) : (
            <div className="text-center text-muted py-3">
              No questions configured for this load.
            </div>
          )}
        </div>
      </Modal>

      <ChecklistFileUploadModal
        open={uploadModal.open}
        onClose={handleCloseUpload}
        onSave={handleSaveUpload}
        title={uploadModal.questionText}
      />
    </>
  );
};

export default FillDispatchChecklistModal;
